import { cn } from "@/utils/helpers"
import { cva, type VariantProps } from "class-variance-authority"
import { forwardRef } from "react"

const tagVariants = cva("flex rounded-full items-center box-border w-fit", {
  variants: {
    variant: {
      neutral:
        "bg-neutral-container text-on-neutral-container border-on-neutral-container [&>div]:bg-on-neutral-container",
      positive:
        "bg-success-container text-on-success-container border-on-success-container [&>div]:bg-on-success-container",
      warning:
        "bg-warning-container text-on-warning-container border-on-warning-container [&>div]:bg-on-warning-container",
      negative:
        "bg-error-container text-on-error-container border-on-error-container [&>div]:bg-on-error-container",
      gray: "bg-outline-variant text-on-surface border-on-surface [&>div]:bg-on-surface",
    },
    size: {
      xs: "px-2 py-1 text-xs",
      sm: "px-2 py-1 text-sm",
      md: "px-3 py-1 text-md",
      lg: "px-3 py-1 text-md",
    },
    hasOutline: {
      true: "border bg-transparent",
      false: "border-0",
    },
  },
  defaultVariants: {
    variant: "neutral",
    size: "sm",
    hasOutline: false,
  },
})

export const TagVariant = {
  NEUTRAL: "neutral",
  POSITIVE: "positive",
  WARNING: "warning",
  NEGATIVE: "negative",
  GRAY: "gray",
} as const

export interface TagProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof tagVariants> {
  label: string
  hasBadge?: boolean
}

const Tag = forwardRef<HTMLDivElement, TagProps>(
  (
    { label, className, variant, size = "sm", hasOutline, hasBadge, ...props },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        className={cn(tagVariants({ variant, size, hasOutline }), className)}
        {...props}
      >
        {hasBadge && (
          <div
            className={cn("-ml-0.5 mr-1.5 h-1.5 w-1.5 rounded-full", {
              "ml-0": size === "sm",
            })}
          />
        )}
        {label}
      </div>
    )
  },
)
Tag.displayName = "Tag"

export { Tag }
